import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Globe, MapPin } from 'lucide-react';
import { adminAPI } from '../services/api';
import { Card, CardHeader, CardTitle, CardContent } from '../components/Card';
import LoadingSpinner from '../components/LoadingSpinner';
import StatCard from '../components/StatCard';

const Geographic = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['geographicDistribution'],
    queryFn: adminAPI.getGeographicDistribution,
  });

  if (isLoading) {
    return <LoadingSpinner text="Loading geographic data..." />;
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-text-primary">Geographic Insights</h1>
        <p className="text-sm sm:text-base text-text-secondary mt-1">
          User distribution across countries, states, and cities
        </p>
      </div>

      {/* Top Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
        <StatCard
          title="Total Countries"
          value={data?.byCountry?.length || 0}
          icon={Globe}
          gradient={true}
        />
        <StatCard
          title="Total States"
          value={data?.byState?.length || 0}
          icon={MapPin}
        />
        <StatCard
          title="Total Cities"
          value={data?.byCity?.length || 0}
          icon={MapPin}
        />
      </div>

      {/* Countries Table */}
      <Card>
        <CardHeader>
          <CardTitle>Top Countries</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-background-elevated border-b border-border">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase">
                    Rank
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase">
                    Country
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase">
                    Users
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data?.byCountry?.slice(0, 20).map((item, index) => (
                  <tr key={index} className="hover:bg-background-elevated transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-bold text-text-primary">#{index + 1}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Globe size={16} className="text-cyan" />
                        <span className="text-sm font-medium text-text-primary">
                          {item.country}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-text-primary">{item.count}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* States and Cities Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* States Table */}
        <Card>
          <CardHeader>
            <CardTitle>Top States/Regions</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto max-h-96">
              <table className="w-full">
                <thead className="bg-background-elevated border-b border-border sticky top-0">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase">
                      State
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase">
                      Country
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase">
                      Users
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {data?.byState?.slice(0, 15).map((item, index) => (
                    <tr key={index} className="hover:bg-background-elevated transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-text-primary">{item.state}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-text-secondary">{item.country}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-text-primary">{item.count}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Cities Table */}
        <Card>
          <CardHeader>
            <CardTitle>Top Cities</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto max-h-96">
              <table className="w-full">
                <thead className="bg-background-elevated border-b border-border sticky top-0">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase">
                      City
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase">
                      State/Country
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase">
                      Users
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {data?.byCity?.slice(0, 15).map((item, index) => (
                    <tr key={index} className="hover:bg-background-elevated transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <MapPin size={14} className="text-primary" />
                          <span className="text-sm font-medium text-text-primary">
                            {item.city}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-text-secondary">
                          {item.state}, {item.country}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-text-primary">{item.count}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Geographic;
